// src/pages/admin/products/RawMaterials.jsx
import { useState, useEffect } from 'react'
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Eye,
  Trash2,
  Package,
  AlertTriangle,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown
} from 'lucide-react'
import { Button } from '../../../components/ui/Button'
import { Input } from '../../../components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card'
import RawMaterialsService from '../../../services/rawMaterialsService'
import UnitsService from '../../../services/unitsService'
import AddRawMaterialModal from '../../../components/modals/AddRawMaterialModal'
import EditRawMaterialModal from '../../../components/modals/EditRawMaterialModal'
import ViewRawMaterialModal from '../../../components/modals/ViewRawMaterialModal'
import DeleteConfirmModal from '../../../components/modals/DeleteConfirmModal'
import InlineLoader from '../../../components/ui/InlineLoader'

export const RawMaterials = () => {
  // State management
  const [rawMaterials, setRawMaterials] = useState([])
  const [units, setUnits] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [unitFilter, setUnitFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [toast, setToast] = useState(null)

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false)
  const [showEditModal, setShowEditModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedRawMaterial, setSelectedRawMaterial] = useState(null)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)

  // Safe getter utility
  const safeGet = (obj, path, defaultValue = null) => {
    try {
      return path.split('.').reduce((current, key) => {
        return current && current[key] !== undefined ? current[key] : defaultValue
      }, obj)
    } catch {
      return defaultValue
    }
  }

  // Helper function to validate raw material object
  const validateRawMaterial = (material) => {
    if (!material || typeof material !== 'object') return false
    return material._id && material.name && material.code
  }

  // Show toast notification
  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Fetch raw materials using service
  const fetchRawMaterials = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Fetch all raw materials
      const response = await RawMaterialsService.getAllRawMaterials()
      
      if (response && response.success) {
        // Handle different possible response structures
        const materialsData = safeGet(response, 'data.rawMaterials', []) ||
                             safeGet(response, 'data.docs', []) ||
                             safeGet(response, 'data', [])
        
        console.log('🧱 Raw materials response structure:', response.data)
        console.log('🧱 Extracted materials data:', materialsData)
        
        // Log sample raw material structure to understand unit field
        if (materialsData.length > 0) {
          console.log('🧱 Sample raw material structure:', materialsData[0])
          console.log('🧱 Sample unit field:', materialsData[0].unit)
          console.log('🧱 Unit field type:', typeof materialsData[0].unit)
        }
        
        // Filter out invalid materials
        const validMaterials = Array.isArray(materialsData)
          ? materialsData.filter(validateRawMaterial)
          : []
        
        console.log('🧱 Valid raw materials loaded:', validMaterials.length)
        setRawMaterials(validMaterials)
      } else {
        console.warn('⚠️ Unexpected response format:', response)
        setError('Failed to load raw materials')
        setRawMaterials([])
      }
    } catch (error) {
      console.error('❌ Error fetching raw materials:', error)
      setError(error.message || 'Failed to load raw materials')
      setRawMaterials([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch units for dropdown
  const fetchUnits = async () => {
    try {
      console.log('📏 Fetching units for dropdown...')
      // Fetch units without pagination first, then with if needed
      const response = await UnitsService.getAllUnits()
      
      console.log('📏 Full units response:', response)
      
      if (response && response.success) {
        // Based on your API response, units are in response.data.units
        const unitsData = response.data?.units || []
        console.log('📏 Units data extracted:', unitsData)
        console.log('📏 Number of units found:', Array.isArray(unitsData) ? unitsData.length : 0)
        
        if (Array.isArray(unitsData) && unitsData.length > 0) {
          console.log('📏 Sample unit structure:', unitsData[0])
          console.log('📏 All unit IDs:', unitsData.map(u => ({ id: u._id, name: u.name })))
          setUnits(unitsData)
        } else {
          console.warn('📏 No units found or units data is not an array:', typeof unitsData)
          setUnits([])
        }
      } else {
        console.warn('⚠️ Failed to fetch units:', response)
        setUnits([])
      }
    } catch (error) {
      console.error('❌ Error fetching units:', error)
      setUnits([]) // Set empty array on error
    }
  }

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      console.log('🚀 Starting data load...')
      // Load units first, then raw materials
      await fetchUnits()
      await fetchRawMaterials()
      console.log('✅ Data load complete')
    }
    
    loadData()
  }, [])

  // Get unit by ID or return unit if it's already an object
  const getUnitById = (unitIdOrObject) => {
    console.log('🔍 Looking for unit:', unitIdOrObject)
    console.log('🔍 Type of unit field:', typeof unitIdOrObject)
    
    // If unitIdOrObject is already a unit object with _id, name, abbreviation
    if (unitIdOrObject && typeof unitIdOrObject === 'object' && unitIdOrObject._id) {
      console.log('🔍 Unit field is already populated object:', unitIdOrObject.name)
      return unitIdOrObject
    }
    
    // If it's a string ID, find the unit in the units array
    if (typeof unitIdOrObject === 'string') {
      console.log('🔍 Searching for unit ID in units array:', unitIdOrObject)
      const foundUnit = units.find(unit => unit._id === unitIdOrObject)
      console.log('🔍 Found unit:', foundUnit ? `${foundUnit.name} (${foundUnit.abbreviation})` : 'NOT FOUND')
      return foundUnit || null
    }
    
    console.log('🔍 Unit field is neither object nor string:', unitIdOrObject)
    return null
  }

  // Filter and search raw materials
  const getFilteredRawMaterials = () => {
    let filtered = [...rawMaterials]

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter(material =>
        safeGet(material, 'name', '').toLowerCase().includes(searchLower) ||
        safeGet(material, 'code', '').toLowerCase().includes(searchLower) ||
        safeGet(material, 'description', '').toLowerCase().includes(searchLower)
      )
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active'
      filtered = filtered.filter(material => material.isActive === isActive)
    }

    // Unit filter
    if (unitFilter !== 'all') {
      filtered = filtered.filter(material => {
        // Handle both string ID and populated object
        const materialUnitId = typeof material.unit === 'object' && material.unit?._id
          ? material.unit._id
          : material.unit
        return materialUnitId === unitFilter
      })
    }

    // Sort
    filtered.sort((a, b) => {
      let valueA = a[sortBy]
      let valueB = b[sortBy]

      if (typeof valueA === 'string') {
        valueA = valueA.toLowerCase()
        valueB = valueB.toLowerCase()
      }

      if (sortOrder === 'asc') {
        return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
      } else {
        return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
      }
    })

    return filtered
  }

  // Get paginated materials
  const getPaginatedMaterials = () => {
    const filtered = getFilteredRawMaterials()
    const startIndex = (currentPage - 1) * itemsPerPage
    const endIndex = startIndex + itemsPerPage
    return filtered.slice(startIndex, endIndex)
  }

  // Get pagination info
  const getPaginationInfo = () => {
    const total = getFilteredRawMaterials().length
    const totalPages = Math.ceil(total / itemsPerPage)
    return { total, totalPages }
  }

  // Handle material added
  const handleMaterialAdded = (newMaterial) => {
    if (validateRawMaterial(newMaterial)) {
      setRawMaterials(prev => [newMaterial, ...prev])
      showToast('Raw material created successfully')
    }
  }

  // Handle material updated
  const handleMaterialUpdated = (updatedMaterial) => {
    if (validateRawMaterial(updatedMaterial)) {
      setRawMaterials(prev =>
        prev.map(material =>
          material._id === updatedMaterial._id ? updatedMaterial : material
        )
      )
      showToast('Raw material updated successfully')
    }
  }

  // Handle material deleted
  const handleMaterialDeleted = async () => {
    if (!selectedRawMaterial) return

    try {
      const response = await RawMaterialsService.deleteRawMaterial(selectedRawMaterial._id)
      
      if (response && response.success) {
        setRawMaterials(prev => prev.filter(material => material._id !== selectedRawMaterial._id))
        showToast('Raw material deleted successfully')
      } else {
        showToast('Failed to delete raw material', 'error')
      }
    } catch (error) {
      console.error('❌ Error deleting raw material:', error)
      showToast(error.message || 'Failed to delete raw material', 'error')
    }

    setShowDeleteModal(false)
    setSelectedRawMaterial(null)
  }

  // Handle status toggle
  const handleStatusToggle = async (material) => {
    try {
      const newStatus = !material.isActive
      const response = await RawMaterialsService.toggleRawMaterialStatus(material._id, newStatus)
      
      if (response && response.success) {
        setRawMaterials(prev =>
          prev.map(m =>
            m._id === material._id ? { ...m, isActive: newStatus } : m
          )
        )
        showToast(`Raw material ${newStatus ? 'activated' : 'deactivated'} successfully`)
      } else {
        showToast('Failed to update status', 'error')
      }
    } catch (error) {
      console.error('❌ Error toggling status:', error)
      showToast(error.message || 'Failed to update status', 'error')
    }
  }

  // Action handlers
  const handleView = (material) => {
    setSelectedRawMaterial(material)
    setShowViewModal(true)
  }

  const handleEdit = (material) => {
    setSelectedRawMaterial(material)
    setShowEditModal(true)
  }

  const handleDelete = (material) => {
    setSelectedRawMaterial(material)
    setShowDeleteModal(true)
  }

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('')
    setStatusFilter('all')
    setUnitFilter('all')
    setCurrentPage(1)
  }

  const { total, totalPages } = getPaginationInfo()
  const paginatedMaterials = getPaginatedMaterials()

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded-md shadow-lg ${
          toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
        }`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Raw Materials</h1>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            onClick={fetchRawMaterials}
            disabled={loading}
            className="flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          
          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Raw Material
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Materials</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{rawMaterials.length}</div>
            <p className="text-xs text-muted-foreground">
              {units.length} units available
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Materials</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {rawMaterials.filter(m => m.isActive).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Inactive Materials</CardTitle>
            <XCircle className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {rawMaterials.filter(m => !m.isActive).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Threshold</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {rawMaterials.length > 0
                ? Math.round(rawMaterials.reduce((sum, m) => sum + (m.threshold || 0), 0) / rawMaterials.length)
                : 0
              }
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="p-6">
          {/* Filter Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Filter className="h-5 w-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Filter Raw Materials</h2>
            </div>
            {(searchTerm || statusFilter !== 'all' || unitFilter !== 'all') && (
              <Button
                variant="outline"
                onClick={clearFilters}
                className="text-orange-500 border-orange-500 hover:bg-orange-50"
              >
                Clear Filters
              </Button>
            )}
          </div>

          {/* Filter Controls */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search raw materials..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-11 border-gray-300"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Unit Filter */}
            <div className="relative">
              <select
                value={unitFilter}
                onChange={(e) => setUnitFilter(e.target.value)}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="all">All Units</option>
                {units.map(unit => (
                  <option key={unit._id} value={unit._id}>
                    {unit.name} ({unit.abbreviation})
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-')
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="w-full h-11 px-3 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 appearance-none bg-white"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="code-asc">Code (A-Z)</option>
                <option value="code-desc">Code (Z-A)</option>
                <option value="threshold-asc">Threshold (Low-High)</option>
                <option value="threshold-desc">Threshold (High-Low)</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            </div>
          </div>

          {/* Filter Summary */}
          <div className="text-sm text-gray-600">
            Showing {paginatedMaterials.length} of {total} raw materials
          </div>
        </CardContent>
      </Card>

      {/* Raw Materials Table */}
      {loading ? (
        <InlineLoader title="Loading raw materials..." py="py-12" />
      ) : error ? (
        <div className="text-center py-12">
          <AlertTriangle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={fetchRawMaterials} variant="outline">
            Try Again
          </Button>
        </div>
      ) : paginatedMaterials.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-4xl mb-4">🧱</div>
          <h3 className="text-lg font-medium mb-2">
            {rawMaterials.length === 0 ? 'No raw materials found' : 'No materials match your filters'}
          </h3>
          <p className="text-gray-500 mb-4">
            {rawMaterials.length === 0
              ? 'Get started by creating your first raw material.'
              : 'Try adjusting your search criteria.'
            }
          </p>
          {rawMaterials.length === 0 && (
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Raw Material
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="min-w-full bg-white">
              {/* Table Header */}
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    MATERIAL
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    CODE
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UNIT
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    UNIT PRICE
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ACTIONS
                  </th>
                </tr>
              </thead>

              {/* Table Body */}
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedMaterials.map((material) => {
                  const unit = getUnitById(material.unit)
                  
                  return (
                    <tr key={material._id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{material.name}</div>
                          {material.description && (
                            <div className="text-sm text-gray-500">{material.description}</div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{material.code}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-6 w-6">
                            <div className="h-6 w-6 rounded-full bg-gray-100 flex items-center justify-center">
                              <Package className="h-3 w-3 text-gray-500" />
                            </div>
                          </div>
                          <div className="ml-2">
                            <div className="text-sm font-medium text-gray-900">
                              {unit ? unit.name : 'Unknown Unit'}
                            </div>
                            <div className="text-xs text-gray-500">
                              {unit ? unit.abbreviation : 'N/A'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          Rs. {material.unitPrice ? material.unitPrice.toFixed(2) : '0.00'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleView(material)}
                            className="text-gray-400 hover:text-gray-600"
                            title="View Details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(material)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Edit Material"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(material)}
                            className="text-gray-400 hover:text-gray-600"
                            title="Delete Material"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="block lg:hidden space-y-4">
            {paginatedMaterials.map((material) => {
              const unit = getUnitById(material.unit)
              
              return (
                <Card key={material._id} className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900">{material.name}</h3>
                      <p className="text-sm text-gray-500 mt-1">{material.code}</p>
                      {material.description && (
                        <p className="text-sm text-gray-500 mt-1">{material.description}</p>
                      )}
                      <div className="flex items-center mt-2">
                        {unit ? (
                          <div className="flex items-center">
                            <div className="h-4 w-4 rounded-full bg-gray-100 flex items-center justify-center mr-1">
                              <Package className="h-2 w-2 text-gray-500" />
                            </div>
                            <span className="text-xs text-gray-600">{unit.name} ({unit.abbreviation})</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-500">Unknown Unit</span>
                        )}
                        <span className="text-xs text-gray-400 mx-2">•</span>
                        <span className="text-xs font-medium text-gray-900">
                          Rs. {material.unitPrice ? material.unitPrice.toFixed(2) : '0.00'}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1 ml-4">
                      <button
                        onClick={() => handleView(material)}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-full"
                        title="View"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleEdit(material)}
                        className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded-full"
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(material)}
                        className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full"
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, total)} of {total} results
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showAddModal && (
        <AddRawMaterialModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onMaterialAdded={handleMaterialAdded}
          units={units}
        />
      )}

      {showEditModal && selectedRawMaterial && (
        <EditRawMaterialModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false)
            setSelectedRawMaterial(null)
          }}
          rawMaterial={selectedRawMaterial}
          onMaterialUpdated={handleMaterialUpdated}
          units={units}
        />
      )}

      {showViewModal && selectedRawMaterial && (
        <ViewRawMaterialModal
          isOpen={showViewModal}
          onClose={() => {
            setShowViewModal(false)
            setSelectedRawMaterial(null)
          }}
          rawMaterial={selectedRawMaterial}
          unit={getUnitById(selectedRawMaterial.unit)}
        />
      )}

      {showDeleteModal && selectedRawMaterial && (
        <DeleteConfirmModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false)
            setSelectedRawMaterial(null)
          }}
          onConfirm={handleMaterialDeleted}
          title="Delete Raw Material"
          message={`Are you sure you want to delete "${selectedRawMaterial.name}"? This action cannot be undone.`}
        />
      )}
    </div>
  )
}